import services from 'services/module';
import {Strings} from 'Strings';
import Intercom from 'intercom';
import {CookieSettings} from 'services/CookieSettings';
import {HttpSvcFactory} from './HttpSvc';
import {NewsSvc} from './NewsSvc';
import {LocalStorageSvc} from './LocalStorageSvc';
import {Utils} from 'underscore-extensions';
import {AnalyticSvc} from './AnalyticSvc';
import {AffiliateSvc} from './AffiliateSvc';

export const USER_LOGOUT = 'user:logout';
export const USER_UPDATE_SUCCESS = 'website/user/updated';

export class AuthenticationSvc {
    animatronCookieName = '_animatronauth';
    signupRedirectPropName = 'signupRedirectUrl';
    loginRedirect = '';
    loggedIn = false;
    loaded = false;
    loginRedirectMessage = '';
    signupModalShown = false;
    loginModalShown = false;
    lastUpdated = _.now();

    constructor(private $rootScope: ng.IRootScopeService,
                private $state: ng.ui.IStateService,
                private $q: ng.IQService,
                private $timeout: ng.ITimeoutService,
                private $location: ng.ILocationService,
                private $log: ng.ILogService,
                private $cacheFactory: ng.ICacheFactoryService,
                private analyticSvc: AnalyticSvc,
                private affiliateSvc: AffiliateSvc,
                private ipCookie: ipCookie,
                private LocalStorageSvc: LocalStorageSvc,
                private HttpSvc: HttpSvcFactory,
                private NewsSvc: NewsSvc) { //TODO do not depend on NewsSvc
        $rootScope.updateUser = this.updateUser;
        $timeout(() => $rootScope.$on('$locationChangeStart', () => {
            this.loginRedirect = '';
            this.closeModals();
        }));
    }

    isCookieEnabled = _.once(() => {
        let enabled;
        const checkerCookieName = '__animatron_check_cookie_enabled';
        this.ipCookie(checkerCookieName, 'isEnabled');
        enabled = 'isEnabled' === this.ipCookie(checkerCookieName);
        this.ipCookie.remove(checkerCookieName);
        return enabled;
    });

    setUserGlobals(user: anm.User): anm.User {
        const getProduct = () => {
            const defaultProducts = 'studio';
            const products = ['studio', 'wave'];
            const currentProduct = Utils.getProduct().toLowerCase();

            return products.includes(currentProduct)
                ? currentProduct
                : defaultProducts;
        };

        user.accountTypes = {} as anm.UseraccountTypes;
        user.features = user.subscriptionDetails[getProduct()].features;
        user.staff = user.subscriptionDetails[getProduct()].name === 'Staff' || user.subscriptionDetails[getProduct()].name === 'Jedi';
        user.meta = user.meta || {};
        user.accountTypes.STUDIO = user.subscriptionDetails.studio.displayName;
        user.accountTypes.WAVE = user.subscriptionDetails.wave.displayName;
        user.isStripe = () => {
            const {hasStripeSubscription, hasStripeUser} = user.subscriptionDetails[getProduct()];
            return hasStripeSubscription || hasStripeUser;
        };
        user.stripeKey = () => user.zoneCode === 'kr' ? options.stripePubKeyKr : options.stripePubKey;
        this.$rootScope.user = user;
        options.user = user;
        options.headers = {'Animatron-Security-Token': user.hash};
        this.NewsSvc.onUserChanged(user);
        this.loaded = true;
        return user;
    }

    setUser(user: anm.User, token: string): ng.IPromise<anm.User> {
        return this.$timeout(() => {
            this.loggedIn = true;
            this.setSubscription(user);
            user.hash = token;
            user.anonymous = user.anonymous || user.username === 'Anonymous';
            if (user.staff) {
                options.debug = true;
            }
            if (window.Raven) {
                Raven.setUserContext({
                    id: user.username,
                    email: user.email,
                    subscription: user.subscriptionDetails.studio.displayName
                });
            }

            this.setUserGlobals(user);
            Intercom.setUser(user);
            this.$rootScope.$emit(USER_UPDATE_SUCCESS);
            this.LocalStorageSvc.set('animatron:secure:user', user);
            return user;
        });
    }

    extendUser(newProps: any): void {
        this.$rootScope.user = angular.merge(options.user, newProps);
        this.LocalStorageSvc.set('animatron:secure:', options.user);
        this.notifyUserChange();
    }

    setAnonymous(): ng.IPromise<anm.User> {
        this.loggedIn = false;
        Intercom.setAnonymous();
        ['user', 'persist:auth', 'persist:user'].forEach(item =>
            this.LocalStorageSvc.remove(item)
          );
        return this.$timeout(() => this.setUserGlobals(options.anonymous));
    }

    getUser(update: boolean): ng.IPromise<anm.User> {
        const userCookieIsPresent = this.ipCookie(this.animatronCookieName);
        const cachedUser = this.LocalStorageSvc.get<anm.User>('animatron:secure:user');
        if (userCookieIsPresent && cachedUser) {
            if (update) {
                this.updateUser();
            }
            return this.setUser(cachedUser, cachedUser.hash);
        } else {
            return this.updateUser();
        }
    }

    updateUser(): ng.IPromise<anm.User> {
        const deferred = this.$q.defer();
        const token = this.ipCookie(this.animatronCookieName);
        options.headers = {'Animatron-Security-Token': token || ''};
        if (token) {
            this.HttpSvc('profile').secureGet().then(r => r.data).then(user => {
                this.lastUpdated = _.now();
                if (Utils.isUser(user)) {
                    this.setUser(user, token).then(deferred.resolve);
                } else {
                    this.clearCookies();
                    deferred.resolve(this.setAnonymous());
                }
            });
        } else {
            deferred.resolve(this.setAnonymous());
        }
        return deferred.promise;
    }

    login(secureUser: anm.SecureUser, loginType = 'Email'): ng.IPromise<anm.SecureUser> {
        try {
            this.ipCookie(this.animatronCookieName, secureUser.encryptedToken, CookieSettings.get());
            this.ipCookie.remove('_animatron_activate_pending', CookieSettings.get());
        } catch (e) {
            this.$log.error('Could not set cookie', e);
        }
        secureUser.userProfile.isNew = secureUser.isNew;
        secureUser.userProfile.social = loginType.toLowerCase() !== 'email';
        return this.setUser(secureUser.userProfile, secureUser.encryptedToken).then(() => {
            this.closeModals();
            secureUser.isNew && this.analyticSvc.signUpSocialComplete(loginType as anm.SignUpProvider);
            this.analyticSvc.login(secureUser.userProfile);
            this.$rootScope.$emit('user:login');
            this.redirectAfterLogin();

            return secureUser;
        });
    }

    loginFromCookie() {
        return this.updateUser().then(() => this.redirectAfterLogin());
    }

    loginWithCookie(token: string): ng.IPromise<anm.User> {
        this.ipCookie(this.animatronCookieName, token, CookieSettings.get());
        return this.updateUser();
    }

    loginOAuth(authToken: string,
               oauthService: string,
               product: AnimatronProduct): ng.IPromise<anm.SecureUser> {
        const deferred = this.$q.defer();

        const productUpper = product ? product.toUpperCase() : Utils.getProduct();
        const animatronProduct = productUpper === 'ANY' ? 'STUDIO' : productUpper;

        const data: any = {
            '@class': 'oauth',
            entry: Utils.getEntry(),
            authToken,
            oauthService,
            product: animatronProduct
        };

        const headers = {
            ...this.analyticSvc.getGaHeader(),
            ...this.affiliateSvc.getAffiliateHeader(),
            ...options.user.hash ? options.headers : {}
        };

        this.HttpSvc('authenticate')
            .post(data, {headers})
            .then(r => r.data)
            .then((response: ApiError | anm.SecureUser) => {
                if (Utils.isApiError(response)) {
                    _.notification.showError(response.message);
                } else {
                    this.loginRedirect = 'none';
                    this.login(response, oauthService).then(deferred.resolve);
                }
            });
        return deferred.promise;
    }

    logout(): ng.IPromise<anm.User> {
        this.loginRedirect = '';
        this.loginRedirectMessage = '';
        this.clearCookies();
        this.analyticSvc.logout();
        this.$cacheFactory.get('$http').removeAll();
        this.$rootScope.$emit(USER_LOGOUT);
        return this.setAnonymous();
    }

    logoutAndGoRoot() {
        const product = Utils.getProduct();
        return this.logout().then(() => {
            const waveRootPath = `https://wave${options.isProduction ? '' : '-test'}.video`;
            const studioRootPath = `https://www.animatron${options.isProduction ? '' : '-test'}.com/studio`;
            const redirectPath = options.isLocal ? '' : product === 'STUDIO' ? studioRootPath : waveRootPath;

            this.shouldRedirect() ? location.href = redirectPath : window.history.back();
        });
    }

    clearCookies() {
        this.ipCookie.remove(this.animatronCookieName, CookieSettings.get());
    }

    notifyUserChange() {
        if (this.isCookieEnabled) {
            this.ipCookie('_animatronchanged', 'yes', CookieSettings.get());
        }
    }

    toggleLoginModal(redirect?: string, message = '') {
        this.loginRedirect = redirect;
        this.loginRedirectMessage = message;
        this.loginModalShown = true;
    }

    openSignUpPage(redirectUrl?: string,
        params = {}) {
        let paramsWithRedirectUrl = params;

        if (redirectUrl) {
            this.LocalStorageSvc.set(this.signupRedirectPropName, redirectUrl);
            paramsWithRedirectUrl = { redirectUrl, ...paramsWithRedirectUrl };
        }

        Utils.redirectToReact(`signup`, undefined, paramsWithRedirectUrl);
    }

    activateEmailRedirect(): boolean {
        const url = this.LocalStorageSvc.remove<string>(this.signupRedirectPropName);

        url && this.redirect(url);

        return !!url;
    }

    openLogin() {
        if (options.handHeld) {
            this.$state.go('login').then(() => this.$timeout(() => this.loginRedirect = 'back'));
        } else {
            this.toggleLoginModal();
        }
    }

    closeModals() {
        this.signupModalShown = false;
        this.loginModalShown = false;
    }

    private setSubscription(user: anm.User) {
        let wave: anm.SubscriptionDetails;
        let studio: anm.SubscriptionDetails;

        if (_.isArray(user.subscriptionDetails)) {
            user.subscriptionDetails.forEach((subscription: anm.SubscriptionDetails) => {
                if (subscription.product === 'WAVE') {
                    wave = subscription;
                } else {
                    studio = subscription;
                }
            });

            user.subscriptionDetails = Object.assign({}, {wave, studio});
        }
    }

    private shouldRedirect() {
        return !this.$location.search().noRedirect && !this.LocalStorageSvc.get('noRedirect');
    }

    private redirect(url: string) {
        Utils.redirect(url, this.$location);
    }

    private getUrlByEntry(entry: anm.AuthEntry, baseUrl: string) {
        return ({
            CALENDAR: `${baseUrl}calendar`,
            TEMPLATE: `${baseUrl}video-templates`
        }[entry]);
    }

    private getUrlByProduct(product: AnimatronProduct, baseUrl: string) {
        return ({
            WAVE: `${baseUrl}my-projects`,
            STUDIO: `${baseUrl}home`
        }[product]);
    }

    private getLoginRedirectUrl(entry: anm.AuthEntry, product: AnimatronProduct, baseUrl: string) {
        return entry === 'LANDING'
            ? this.getUrlByProduct(product, baseUrl)
            : this.getUrlByEntry(entry, baseUrl);
    }

    private redirectAfterLogin() {
        const { entry } = options.user.meta;
        const product = Utils.getProduct();
        const url = this.getLoginRedirectUrl(entry, product, '/');

        const thirdPartyRedirect = this.LocalStorageSvc.remove<string>('_animatron_redirect_onlogin');

        if (thirdPartyRedirect) {
            location.href = thirdPartyRedirect;
        } else if (this.loginRedirect === 'none') {
            // do nothing
        } else if (this.loginRedirect === 'back') {
            window.history.back();
        } else if (this.loginRedirect) {
            this.redirect(this.loginRedirect);
        } else if (this.shouldRedirect()) {
            this.$location.path(url);
        }
    }
}

services.service('AuthenticationSvc', AuthenticationSvc);



// WEBPACK FOOTER //
// ./app/assets/javascripts/services/AuthenticationSvc.ts